import { type ChangeEvent, type FC, type InputHTMLAttributes, type Ref } from "react";
import { FieldError } from "react-hook-form";
import { cn } from "../../lib/utils";
import { FormFieldError } from "../FormFieldError";

export type TextAreaInputProps = Omit<
	InputHTMLAttributes<HTMLTextAreaElement>,
	"readOnly" | "prefix"
> & {
	onChange?: (
		event: ChangeEvent<HTMLTextAreaElement> & {
			target: ChangeEvent<HTMLTextAreaElement>["target"] & {
				value: ChangeEvent<HTMLTextAreaElement>["target"]["value"] | null;
			};
		},
	) => void;
	error?: FieldError;
	invalid?: boolean;
	readonly?: boolean;
	ref?: Ref<HTMLTextAreaElement>;
};

export const TextAreaInput: FC<TextAreaInputProps> = ({
	className,
	type,
	error,
	invalid = false,
	readonly,
	ref,
	...props
}) => {
	return (
		<div
			className={cn(
				"relative flex w-full h-full rounded-md outline outline-[1px] outline-offset-[-1px] text-primary outline-gray-300 bg-gray-50 bg-opacity-70 text-[14px] leading-[20px] placeholder:text-tertiary focus-within:outline-gray-500 disabled:cursor-not-allowed disabled:opacity-50",
				className,
				readonly ? "opacity-80" : undefined,
				error || invalid ? "outline-red-500 focus-within:outline-red-500" : undefined,
			)}
		>
			<textarea
				disabled={readonly}
				tabIndex={0}
				className={cn(
					"resize-none px-[13px] py-[10px] w-full h-full outline-none bg-transparent",
					readonly ? "bg-gray-100 text-gray-800" : undefined,
				)}
				{...props}
				ref={ref}
				// Needed to disable browser auto-filling
				autoComplete="one-time-code"
				data-1p-ignore
				data-lpignore
			/>
			<FormFieldError error={error} />
		</div>
	);
};
